<template>
  <Teleport to="body">
    <Transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center overflow-hidden overscroll-none bg-black/40 backdrop-blur-[1px]"
        @click.self="handleClose"
      >
        <div class="absolute inset-0" aria-hidden="true"></div>
        <div class="absolute inset-0 flex items-center justify-center p-4" @click.stop>
          <div
            class="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-gray-200/80"
            role="dialog"
            aria-modal="true"
          >
            <!-- Header -->
            <div class="flex items-center justify-between gap-3 border-b border-gray-200 px-6 py-4">
              <h2 class="truncate text-xl font-semibold text-gray-800">
                {{ $t('availability.modal.title') }}
              </h2>
              <button
                type="button"
                class="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                :aria-label="$t('admin.close')"
                @click="handleClose"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <!-- Body (scroll) -->
            <div class="min-h-0 flex-1 overflow-auto px-6 py-6">
              <!-- Stepper -->
              <div class="mb-6">
                <div class="flex items-center justify-between gap-4">
                  <div class="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <div class="flex items-center gap-2">
                      <div
                        class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold"
                        :class="step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'"
                      >1</div>
                      <span class="text-sm font-medium text-gray-800">{{ $t('availability.modal.step1Title') }}</span>
                    </div>
                    <div class="h-px w-8 bg-gray-200"></div>
                    <div class="flex items-center gap-2">
                      <div
                        class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold"
                        :class="step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'"
                      >2</div>
                      <span class="text-sm font-medium text-gray-800">{{ $t('availability.modal.step2Title') }}</span>
                    </div>
                    <div class="h-px w-8 bg-gray-200"></div>
                    <div class="flex items-center gap-2">
                      <div
                        class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold"
                        :class="step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'"
                      >3</div>
                      <span class="text-sm font-medium text-gray-800">{{ $t('availability.modal.step3Title') }}</span>
                    </div>
                    <div class="h-px w-8 bg-gray-200"></div>
                    <div class="flex items-center gap-2">
                      <div
                        class="flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold"
                        :class="step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'"
                      >4</div>
                      <span class="text-sm font-medium text-gray-800">{{ $t('availability.modal.step4Title') }}</span>
                    </div>
                  </div>
                  <div class="shrink-0 text-sm text-gray-500">{{ step }}/4</div>
                </div>
              </div>

              <!-- Step 1 -->
              <div v-if="step === 1" class="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div class="rounded-lg bg-gray-100 p-4">
                  <div class="flex items-center justify-between gap-3">
                    <div class="min-w-0">
                      <p class="text-sm text-gray-500">{{ $t('availability.modal.room') }}</p>
                      <p class="truncate text-lg font-semibold text-gray-900">{{ room.roomNumber }}</p>
                    </div>
                  </div>
                  <div class="mt-3 space-y-1 text-sm text-gray-700">
                    <div>
                      <span class="text-gray-500">{{ $t('availability.modal.from') }}:</span>
                      <span class="ml-1 font-medium">{{ computedCheckInLabel }}</span>
                    </div>
                    <div>
                      <span class="text-gray-500">{{ $t('availability.modal.to') }}:</span>
                      <span class="ml-1 font-medium">{{ computedCheckOutLabel }}</span>
                    </div>
                    <div>
                      <span class="text-gray-500">{{ $t('availability.duration') }}:</span>
                      <span class="ml-1 font-medium">{{ durationMonths }} {{ $t('availability.month') }}</span>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">{{ $t('availability.modal.from') }} *</label>
                    <div class="flex gap-2">
                      <select
                        v-model.number="form.startMonth"
                        required
                        class="h-9 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
                      </select>
                      <select
                        v-model.number="form.startYear"
                        required
                        class="h-9 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">{{ $t('availability.modal.to') }} *</label>
                    <div class="flex gap-2">
                      <select
                        v-model.number="form.endMonth"
                        required
                        class="h-9 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
                      </select>
                      <select
                        v-model.number="form.endYear"
                        required
                        class="h-9 flex-1 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Step 2 -->
              <div v-else-if="step === 2" class="space-y-4">
                <label class="block text-sm font-medium text-gray-700">{{ $t('availability.modal.step2Prompt') }}</label>
                <div class="space-y-2">
                  <label
                    class="flex cursor-pointer items-start gap-3 rounded-lg border p-4 hover:bg-gray-50"
                    :class="contactMode === 'anonymous' ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'"
                  >
                    <input v-model="contactMode" type="radio" value="anonymous" class="mt-1" />
                    <div>
                      <div class="font-medium text-gray-900">{{ $t('availability.modal.choiceAnonymousTitle') }}</div>
                      <div class="text-sm text-gray-600">{{ $t('availability.modal.choiceAnonymousBody') }}</div>
                    </div>
                  </label>
                  <label
                    class="flex cursor-pointer items-start gap-3 rounded-lg border p-4 hover:bg-gray-50"
                    :class="contactMode === 'contact' ? 'border-blue-400 ring-1 ring-blue-200' : 'border-gray-200'"
                  >
                    <input v-model="contactMode" type="radio" value="contact" class="mt-1" />
                    <div>
                      <div class="font-medium text-gray-900">{{ $t('availability.modal.choiceContactTitle') }}</div>
                      <div class="text-sm text-gray-600">{{ $t('availability.modal.choiceContactBody') }}</div>
                    </div>
                  </label>
                </div>
              </div>

              <!-- Step 3 -->
              <div v-else-if="step === 3" class="space-y-4">
                <div v-if="contactMode === 'anonymous'" class="space-y-3">
                  <p class="text-gray-700">{{ $t('availability.modal.anonymousCodeStepIntro') }}</p>
                  <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-600">{{ $t('availability.modal.reservationCodeLabel') }}</p>
                    <p class="text-2xl font-semibold tracking-widest text-gray-900">{{ reservationCode }}</p>
                  </div>
                </div>

                <div v-else class="space-y-4">
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">{{ $t('availability.modal.nickname') }}</label>
                    <input
                      v-model="form.nickname"
                      type="text"
                      class="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <p class="text-xs text-gray-500">{{ $t('availability.modal.contactDetailsHint') }}</p>
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">{{ $t('availability.modal.email') }}</label>
                    <input
                      v-model="form.email"
                      type="email"
                      class="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">{{ $t('availability.modal.phone') }}</label>
                    <input
                      v-model="form.phone"
                      type="tel"
                      class="h-9 w-full rounded-lg border border-gray-300 px-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label class="mb-2 block text-sm font-medium text-gray-700">{{ $t('availability.modal.notes') }}</label>
                    <textarea
                      v-model="form.notes"
                      rows="3"
                      class="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <!-- Step 4 -->
              <div v-else class="space-y-4">
                <div class="rounded-lg bg-gray-100 p-4">
                  <div class="flex items-center justify-between gap-3">
                    <div class="min-w-0">
                      <p class="text-sm text-gray-500">{{ $t('availability.modal.room') }}</p>
                      <p class="truncate text-lg font-semibold text-gray-900">{{ room.roomNumber }}</p>
                    </div>
                  </div>
                  <div class="mt-3 space-y-1 text-sm text-gray-700">
                    <div>
                      <span class="text-gray-500">{{ $t('availability.modal.from') }}:</span>
                      <span class="ml-1 font-medium">{{ computedCheckInLabel }}</span>
                    </div>
                    <div>
                      <span class="text-gray-500">{{ $t('availability.modal.to') }}:</span>
                      <span class="ml-1 font-medium">{{ computedCheckOutLabel }}</span>
                    </div>
                    <div>
                      <span class="text-gray-500">{{ $t('availability.duration') }}:</span>
                      <span class="ml-1 font-medium">{{ durationMonths }} {{ $t('availability.month') }}</span>
                    </div>
                  </div>
                </div>
                <template v-if="contactMode === 'anonymous'">
                  <div class="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <p class="text-sm text-gray-600">{{ $t('availability.modal.reservationCodeLabel') }}</p>
                    <p class="text-2xl font-semibold tracking-widest text-gray-900">{{ reservationCode }}</p>
                  </div>
                  <p class="text-gray-700 whitespace-pre-line">{{ $t('availability.modal.anonymousInstructions') }}</p>
                  <div v-if="hasManagerContact" class="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-1">
                    <p v-if="manager.companyName" class="text-sm font-medium text-gray-800">{{ manager.companyName }}</p>
                    <p v-if="manager.phone" class="text-sm text-gray-700">
                      <a :href="phoneHref" class="text-blue-700 hover:underline">{{ manager.phone }}</a>
                    </p>
                    <p v-if="manager.email" class="text-sm text-gray-700">
                      <a :href="emailHref" class="text-blue-700 hover:underline">{{ manager.email }}</a>
                    </p>
                  </div>
                </template>
                <template v-else>
                  <p class="text-gray-700">{{ $t('availability.modal.successMessage') }}</p>
                </template>
              </div>

              <div v-if="error" class="mt-6 rounded-lg border border-red-200 bg-red-50 p-4">
                <p class="text-sm text-red-700">{{ error }}</p>
              </div>
            </div>

            <!-- Sticky footer actions -->
            <div class="flex flex-wrap items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
              <button
                v-if="step > 1 && step < 4"
                type="button"
                class="inline-flex h-9 items-center justify-center rounded-lg bg-gray-100 px-4 text-sm font-medium text-gray-800 hover:bg-gray-200 disabled:opacity-50"
                :disabled="isSubmitting"
                @click="goPrev"
              >
                <svg class="mr-2 h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                {{ $t('admin.previous') }}
              </button>

              <button
                v-if="step === 1"
                type="button"
                class="inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isSubmitting"
                @click="goNextFromStep1"
              >
                <svg class="mr-2 h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
                {{ $t('admin.next') }}
              </button>
              <button
                v-else-if="step === 2"
                type="button"
                class="inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isSubmitting"
                @click="goNextFromStep2"
              >
                <svg class="mr-2 h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
                {{ $t('admin.next') }}
              </button>
              <button
                v-else-if="step === 3 && contactMode === 'contact'"
                type="button"
                class="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isSubmitting"
                @click="submitContactAndFinish"
              >
                <svg v-if="isSubmitting" class="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <svg v-else class="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                {{ $t('availability.modal.confirm') }}
              </button>
              <button
                v-else-if="step === 3 && contactMode === 'anonymous'"
                type="button"
                class="inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                :disabled="isSubmitting"
                @click="step = 4"
              >
                <svg class="mr-2 h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
                {{ $t('availability.modal.nextAfterCode') }}
              </button>
              <button
                v-else-if="step === 4"
                type="button"
                class="inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white hover:bg-blue-700"
                @click="handleSuccessClose"
              >
                <svg class="mr-2 h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                {{ $t('availability.modal.ok') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useApi } from '../../composables/useApi';
import { usePropertyManagerContact } from '../../composables/usePropertyManagerContact.js';
import { meetsMinimumBookingDuration, meetsMaximumBookingDuration } from '../../utils/bookingDuration';
import { firstDayOfMonthIso, lastDayOfMonthIso } from '@merlot/common/bookingMonthDates.js';
import { GLOBAL_SETTINGS_DEFAULTS } from '@merlot/common/globalSettingsDefaults.js';
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  room: {
    type: Object,
    required: true
  },
  checkIn: {
    type: String,
    required: false,
    default: null
  },
  checkOut: {
    type: String,
    required: false,
    default: null
  },
  defaultContactMode: {
    type: String,
    default: 'contact',
    validator: (value) => ['contact', 'anonymous'].includes(value)
  },
  minimumBookingDurationMonths: {
    type: Number,
    default: GLOBAL_SETTINGS_DEFAULTS.minimumBookingDurationMonths
  },
  maximumBookingDurationMonths: {
    type: Number,
    default: GLOBAL_SETTINGS_DEFAULTS.maximumBookingDurationMonths
  }
});

const emit = defineEmits(['close', 'success']);

const { t, locale } = useI18n();
const api = useApi();
const {
  manager: managerContact,
  hasContact: hasManagerContact,
  emailHref,
  phoneHref
} = usePropertyManagerContact();
const manager = computed(() => managerContact.value || {});

const reservationCode = ref(null);
const error = ref(null);
const isSubmitting = ref(false);
const step = ref(1);
const contactMode = ref(props.defaultContactMode); // 'contact' | 'anonymous'

const today = new Date();
const currentYear = today.getFullYear();
const currentMonth = today.getMonth() + 1;

// Month labels follow the active UI locale.
const monthOptions = computed(() => {
  const formatter = new Intl.DateTimeFormat(locale.value, { month: 'long' });
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: formatter.format(new Date(2024, i, 1))
  }));
});

const yearOptions = computed(() => {
  const yearSpan = Math.max(1, Math.ceil(props.maximumBookingDurationMonths / 12) + 1);
  const years = new Set(Array.from({ length: yearSpan }, (_, i) => currentYear + i));
  const start = parseDateToMonthYear(props.checkIn);
  const end = parseDateToMonthYear(props.checkOut);
  if (start) years.add(start.year);
  if (end) years.add(end.year);
  return [...years].sort((a, b) => a - b);
});

function parseDateToMonthYear(dateStr) {
  if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const [y, m] = dateStr.split('-').map(Number);
  return { month: m, year: y };
}

const form = reactive({
  startMonth: currentMonth,
  startYear: currentYear,
  endMonth: currentMonth,
  endYear: currentYear,
  nickname: '',
  email: '',
  phone: '',
  notes: ''
});

const computedCheckIn = computed(() => firstDayOfMonthIso(form.startYear, form.startMonth));
const computedCheckOut = computed(() => lastDayOfMonthIso(form.endYear, form.endMonth));

const computedCheckInLabel = computed(() => {
  const m = monthOptions.value.find(o => o.value === form.startMonth);
  return m ? `${m.label} ${form.startYear}` : '';
});
const computedCheckOutLabel = computed(() => {
  const m = monthOptions.value.find(o => o.value === form.endMonth);
  return m ? `${m.label} ${form.endYear}` : '';
});

const durationMonths = computed(() => {
  const startIndex = form.startYear * 12 + (form.startMonth - 1);
  const endIndex = form.endYear * 12 + (form.endMonth - 1);
  const months = endIndex - startIndex + 1;
  return Math.max(0, months);
});

function syncFormFromProps() {
  const start = parseDateToMonthYear(props.checkIn);
  const end = parseDateToMonthYear(props.checkOut);
  if (start) {
    form.startMonth = start.month;
    form.startYear = start.year;
  } else {
    form.startMonth = currentMonth;
    form.startYear = currentYear;
  }
  if (end) {
    form.endMonth = end.month;
    form.endYear = end.year;
  } else {
    form.endMonth = form.startMonth;
    form.endYear = form.startYear;
  }
  form.nickname = '';
  form.email = '';
  form.phone = '';
  form.notes = '';
  error.value = null;
  reservationCode.value = null;
  isSubmitting.value = false;
  step.value = 1;
  contactMode.value = props.defaultContactMode;
}

// Reset form when modal opens. Use immediate: true so we also run when
// the modal is first mounted with isOpen=true (otherwise the watch never fires).
watch(() => props.isOpen, (newValue) => {
  if (newValue) syncFormFromProps();
}, { immediate: true });

function handleClose() {
  if (!isSubmitting.value) {
    emit('close');
  }
}

function handleSuccessClose() {
  emit('success');
  emit('close');
}

function validateDates() {
  const checkIn = computedCheckIn.value;
  const checkOut = computedCheckOut.value;
  if (!checkIn || !checkOut) {
    error.value = t('availability.fillAllFields');
    return null;
  }
  if (checkOut <= checkIn) {
    error.value = t('availability.invalidDates');
    return null;
  }
  if (!meetsMinimumBookingDuration(checkIn, checkOut, props.minimumBookingDurationMonths)) {
    error.value = t('availability.minimumDurationTooShort', { n: props.minimumBookingDurationMonths });
    return null;
  }
  if (!meetsMaximumBookingDuration(checkIn, checkOut, props.maximumBookingDurationMonths)) {
    error.value = t('availability.maximumDurationTooLong', { n: props.maximumBookingDurationMonths });
    return null;
  }
  return { checkIn, checkOut };
}

function goPrev() {
  error.value = null;
  if (step.value <= 1) return;
  // If user goes back from step 3 anonymous, keep code but allow changing choice.
  step.value -= 1;
}

function goNextFromStep1() {
  error.value = null;
  const dates = validateDates();
  if (!dates) return;
  step.value = 2;
}

async function goNextFromStep2() {
  error.value = null;
  const dates = validateDates();
  if (!dates) return;

  if (contactMode.value === 'anonymous') {
    isSubmitting.value = true;
    try {
      const { checkIn, checkOut } = dates;
      const res = await api.post(`/rooms/${props.room._id}/booking`, {
        checkIn,
        checkOut,
        isAnonymous: true
      });
      reservationCode.value = res?.data?.reservationCode ?? null;
      if (!reservationCode.value) {
        throw new Error(t('availability.modal.error'));
      }
      step.value = 3;
    } catch (e) {
      error.value = e.message || t('availability.modal.error');
    } finally {
      isSubmitting.value = false;
    }
    return;
  }

  step.value = 3;
}

async function submitContactAndFinish() {
  error.value = null;
  isSubmitting.value = true;

  const dates = validateDates();
  if (!dates) {
    isSubmitting.value = false;
    return;
  }
  const { checkIn, checkOut } = dates;

  try {
    const email = form.email ? form.email.trim() : '';
    const phone = form.phone ? form.phone.trim() : '';
    if (!email && !phone) {
      error.value = t('availability.modal.contactDetailsRequired');
      return;
    }

    await api.post(`/rooms/${props.room._id}/booking`, {
      checkIn,
      checkOut,
      ...(form.nickname && form.nickname.trim() ? { nickname: form.nickname.trim() } : {}),
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
      ...(form.notes ? { notes: form.notes.trim() } : {})
    });

    step.value = 4;
  } catch (e) {
    error.value = e.message || t('availability.modal.error');
  } finally {
    isSubmitting.value = false;
  }
}

</script>



<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .bg-white,
.modal-leave-active .bg-white {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-enter-from .bg-white,
.modal-leave-to .bg-white {
  transform: scale(0.9);
  opacity: 0;
}
</style>

